import { z } from "zod";
export const CHANNEL_OBJECT_FIELD_TYPES = [
    "text",
    "number",
    "currency",
    "percent",
    "boolean",
    "date",
    "datetime",
    "select",
    "multiselect",
    "reference",
    "json",
    "user",
    "multiuser",
    "file",
    "artifact",
    "session",
    "url",
    "email",
];
export const channelObjectFieldTypeSchema = z.enum(CHANNEL_OBJECT_FIELD_TYPES);
export const channelObjectFieldTypeDefinitionSchema = z.object({
    type: channelObjectFieldTypeSchema,
    useWhen: z.string().trim().min(1),
    valueShape: z.string().trim().min(1),
    configHint: z.string().trim().min(1).optional(),
}).strict();
/** Canonical agent/API guidance. Prefer a dedicated semantic type over text. */
export const CHANNEL_OBJECT_FIELD_TYPE_CATALOG = [
    { type: "text", useWhen: "Free-form text that is not a URL, email, date/time, number, choice, person, file, or record reference.", valueShape: "string", configHint: "Set display to single_line or multiline when presentation matters." },
    { type: "number", useWhen: "A finite numeric quantity without currency or percentage semantics.", valueShape: "number", configHint: "Use precision for decimal display." },
    { type: "currency", useWhen: "A monetary amount.", valueShape: "number", configHint: "Set currency (ISO code) or symbol and optional precision. When each record has its own currency, set currencyFieldKey to the field holding its ISO code instead." },
    { type: "percent", useWhen: "A rate, ratio, margin, or percentage.", valueShape: "number", configHint: "Set percentScale to fraction for 0.25 = 25% or points for 25 = 25%. Set display to confidence when the value is how sure a judgement is, such as Twin Judge's confidence: it shows as a coloured badge." },
    { type: "boolean", useWhen: "A true/false flag.", valueShape: "boolean" },
    { type: "date", useWhen: "A calendar date with no time-of-day meaning.", valueShape: "YYYY-MM-DD string or epoch milliseconds" },
    { type: "datetime", useWhen: "A timestamp or date with a meaningful time-of-day.", valueShape: "ISO 8601 datetime string, preferably with an explicit offset, or epoch milliseconds" },
    { type: "select", useWhen: "Exactly one value from a controlled list.", valueShape: "configured option string", configHint: "Set config.options." },
    { type: "multiselect", useWhen: "Zero or more values from a controlled list.", valueShape: "array of configured option strings", configHint: "Set config.options." },
    { type: "reference", useWhen: "A link to another MLX channel record.", valueShape: "channel record ID", configHint: "Set targetObjectTypeId." },
    { type: "json", useWhen: "Structured object or array data with no more specific field type.", valueShape: "JSON value" },
    { type: "user", useWhen: "Exactly one organisation member.", valueShape: "canonical organisation user ID", configHint: "Set notifyOnAssignment: true only when this field assigns responsibility; new assignees are notified." },
    { type: "multiuser", useWhen: "Zero or more organisation members.", valueShape: "array of canonical organisation user IDs", configHint: "Set notifyOnAssignment: true only when this field assigns responsibility; new assignees are notified." },
    { type: "file", useWhen: "A file attached to or referenced by the record.", valueShape: "file ID; selecting an existing file does not change its access" },
    { type: "artifact", useWhen: "A document, sheet, or other MLX artifact associated with this record.", valueShape: "artifact ID; opens the current accepted version" },
    { type: "session", useWhen: "An MLX session associated with this record.", valueShape: "session ID; selecting a session does not start a run or change its access" },
    { type: "url", useWhen: "An absolute web URL, including LinkedIn or other profile links.", valueShape: "http:// or https:// URL string" },
    { type: "email", useWhen: "An email address.", valueShape: "email address string" },
];
/**
 * Whether a field may change from one type to the other keeping every stored value: the two hold the same shape of
 * value and differ only in how it reads (a number becoming money, "1434.5" reading "£1,434.50").
 */
export function channelFieldTypesShareValues(from, to) {
    if (from === to)
        return true;
    const shape = (type) => CHANNEL_OBJECT_FIELD_TYPE_CATALOG.find((entry) => entry.type === type)?.valueShape ?? null;
    return shape(from) !== null && shape(from) === shape(to);
}
export const CHANNEL_OBJECT_FIELD_TYPE_SELECTION_GUIDANCE = "Choose the narrowest semantic type that matches the data. Do not use text for URLs, email addresses, dates, timestamps, money, percentages, booleans, controlled choices, users, files, artifacts, sessions, or record references.";
export function suggestChannelObjectFieldType(input) {
    const label = input.label.trim().toLowerCase().replace(/[^a-z0-9]+/gu, " ");
    if (input.fixedChoices)
        return "select";
    if (/\b(email|e-mail)\b/u.test(label))
        return "email";
    if (/\b(linkedin|url|website|web link|profile link)\b/u.test(label))
        return "url";
    if (/\b(owner list|owners|assignees|assigned owners|team members)\b/u.test(label)) {
        return "multiuser";
    }
    if (/\b(owner|assignee|assigned user)\b/u.test(label))
        return "user";
    if (/\b(percent|percentage|probability|margin|rate)\b/u.test(label))
        return "percent";
    if (/\b(currency|money|amount|total|value|price|cost|revenue|spend)\b/u.test(label)) {
        return "currency";
    }
    if (/\b(datetime|timestamp|meeting time|follow-up time|follow up time|time of day)\b/u.test(label)) {
        return "datetime";
    }
    if (/\b(date|day)\b/u.test(label))
        return "date";
    return null;
}
